/* eslint-disable react/no-unescaped-entities */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Wrench, Home, ArrowRight, Lock } from 'lucide-react';

const PROFILES = [
  {
    id: 'seller',
    icon: Home,
    title: 'Vendeur ou propriétaire',
    subtitle: 'Vous vendez un bien immobilier',
    description: 'Identifiez une agence ou un agent certifié qui vous correspond, comparez les profils vérifiés et suivez votre vente en transparence.',
    cta: 'Rechercher un professionnel',
    path: '/recherche',
    gradient: 'from-[#FF914D] to-[#f97316]',
    glowColor: 'rgba(255, 145, 77, 0.15)',
    iconBg: 'bg-[#FF914D]/20',
    iconColor: 'text-[#FF914D]',
    borderHover: 'hover:border-[#FF914D]/50',
  },
  {
    id: 'agency',
    icon: Building2,
    title: 'Professionnel de l\'immobilier',
    subtitle: 'Groupe · Agence · Agent',
    description: 'Structurez votre suivi mandat, pilotez la performance de vos équipes et valorisez la relation vendeur avec un cockpit unifié.',
    cta: 'Découvrir la plateforme',
    path: '/agences',
    gradient: 'from-[#681793] to-[#9b4dca]',
    glowColor: 'rgba(104, 23, 147, 0.15)',
    iconBg: 'bg-[#681793]/20',
    iconColor: 'text-[#a855f7]',
    borderHover: 'hover:border-[#681793]/50',
  },
  {
    id: 'artisan',
    icon: Wrench,
    title: 'Artisan du bâtiment',
    subtitle: 'Partenaires travaux post-vente',
    description: 'Recevez des chantiers qualifiés post-transaction et développez votre activité avec le réseau Homly Partenaires.',
    cta: 'Bientôt disponible',
    path: '/pro',
    gradient: 'from-[#10b981] to-[#059669]',
    glowColor: 'rgba(16, 185, 129, 0.15)',
    iconBg: 'bg-emerald-500/20',
    iconColor: 'text-emerald-400',
    borderHover: 'hover:border-emerald-500/50',
    locked: true,
  },
];

function ProfileCard({ profile, index }) {
  const navigate = useNavigate();
  const Icon = profile.icon;
  const isLocked = profile.locked;

  const handleClick = () => {
    if (!isLocked) navigate(profile.path);
  };

  return (
    <motion.button
      data-testid={`welcome-card-${profile.id}`}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 + index * 0.15, ease: [0.22, 1, 0.36, 1] }}
      onClick={handleClick}
      className={`group relative w-full text-left rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm p-6 sm:p-8 transition-all duration-500 ${isLocked ? 'cursor-default opacity-60' : `cursor-pointer ${profile.borderHover} hover:bg-white/[0.04]`}`}
      whileHover={isLocked ? {} : { y: -4, transition: { duration: 0.3 } }}
      whileTap={isLocked ? {} : { scale: 0.98 }}
    >
      {/* Glow effect on hover */}
      {!isLocked && (
        <div
          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{ boxShadow: `inset 0 1px 0 0 rgba(255,255,255,0.05), 0 0 80px ${profile.glowColor}` }}
        />
      )}

      {/* Lock badge */}
      {isLocked && (
        <div className="absolute top-4 right-4 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.06] border border-white/[0.08]">
          <Lock className="w-3 h-3 text-white/40" />
          <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider">Bientôt</span>
        </div>
      )}

      <div className="relative z-10">
        {/* Icon */}
        <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl ${profile.iconBg} flex items-center justify-center mb-5 transition-transform duration-300 ${!isLocked ? 'group-hover:scale-110' : ''}`}>
          <Icon className={`w-6 h-6 sm:w-7 sm:h-7 ${profile.iconColor}`} />
        </div>

        {/* Title */}
        <h3 className="text-lg sm:text-xl font-bold text-white mb-1 tracking-tight">
          {profile.title}
        </h3>
        <p className="text-xs sm:text-sm text-white/40 mb-3 font-medium">
          {profile.subtitle}
        </p>

        {/* Description */}
        <p className="text-sm text-white/50 leading-relaxed mb-6">
          {profile.description}
        </p>

        {/* CTA */}
        {isLocked ? (
          <span className="inline-flex items-center gap-2 text-sm font-medium text-white/30">
            <Lock className="w-3.5 h-3.5" />
            {profile.cta}
          </span>
        ) : (
          <span className={`inline-flex items-center gap-2 text-sm font-semibold bg-gradient-to-r ${profile.gradient} bg-clip-text text-transparent`}>
            {profile.cta}
            <ArrowRight className={`w-4 h-4 ${profile.iconColor} transition-transform duration-300 group-hover:translate-x-1`} />
          </span>
        )}
      </div>
    </motion.button>
  );
}

export default function WelcomePage() {
  useEffect(() => {
    document.documentElement.classList.add('dark');
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) metaThemeColor.setAttribute('content', '#0a0a1a');
    const prevTitle = document.title;
    document.title = 'Homly — La plateforme immobilière de confiance';
    return () => { document.title = prevTitle; };
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a1a] flex flex-col items-center justify-center relative overflow-hidden px-4 py-12">
      {/* Background orbs */}
      <motion.div
        className="absolute w-[500px] h-[500px] rounded-full blur-[120px] bg-[#681793] top-[-10%] left-[-10%]"
        animate={{ scale: [1, 1.15, 1], opacity: [0.12, 0.2, 0.12] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute w-[400px] h-[400px] rounded-full blur-[120px] bg-[#FF914D] bottom-[-10%] right-[-10%]"
        animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.18, 0.1] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute w-[300px] h-[300px] rounded-full blur-[100px] bg-[#10b981] bottom-[20%] left-[30%]"
        animate={{ scale: [1, 1.1, 1], opacity: [0.05, 0.1, 0.05] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:60px_60px]" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-4xl mx-auto">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center mb-10 sm:mb-14"
        >
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-3">
            Bienvenue sur{' '}
            <span className="bg-gradient-to-r from-[#681793] via-[#9b4dca] to-[#FF914D] bg-clip-text text-transparent">
              Homly
            </span>
          </h1>
          <p className="text-base sm:text-lg text-white/40 font-medium max-w-md mx-auto">
            Sélectionnez votre profil pour accéder à l'expérience adaptée.
          </p>
        </motion.div>

        {/* Profile cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {PROFILES.map((profile, i) => (
            <ProfileCard key={profile.id} profile={profile} index={i} />
          ))}
        </div>

        {/* Bottom text */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="text-center text-xs text-white/20 mt-10 sm:mt-14"
        >
          L'immobilier, simplifié.
        </motion.p>
      </div>
    </div>
  );
}
